// src/admin/pages/reservation/ReservationDecisionDrawer.tsx
// Confirms an Approve/Reject decision on a reservation/test-drive request.
// A reason/note is required and is included in the customer's notification
// email (sent from the Cloud Functions side — see
// functions/src/reservationNotifications.ts) when the customer provided an
// email address.
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  DirectionsCar as CarIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { Reservation } from '../../../enums/reservation';
import DrawerFooter from '../../components/DrawerFooter';

export type Decision = 'Approved' | 'Rejected';

interface ReservationDecisionDrawerProps {
  open: boolean;
  reservation: Reservation | null;
  decision: Decision | null;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}

const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <Stack direction="row" alignItems="flex-start" spacing={1.5}>
    <Box sx={{ color: 'text.secondary', display: 'flex', mt: '2px' }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  </Stack>
);

const ReservationDecisionDrawer: React.FC<ReservationDecisionDrawerProps> = ({
  open,
  reservation,
  decision,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setReason('');
      setTouched(false);
    }
  }, [open]);

  if (!reservation || !decision) return null;

  const isApprove = decision === 'Approved';
  const accentColor = isApprove ? 'success' : 'error';
  const vehicleLabel =
    reservation.carName ||
    (reservation.modelName ? `${reservation.brandName ? reservation.brandName + ' ' : ''}${reservation.modelName} (any unit)` : 'No specific vehicle');

  const reasonError = touched && reason.trim() === '';

  const handleConfirm = async () => {
    if (reason.trim() === '') {
      setTouched(true);
      return;
    }
    setSaving(true);
    try {
      await onConfirm(reason.trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: '100%', md: 460 }, maxWidth: 460 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* ── Header ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6">{isApprove ? 'Approve Reservation' : 'Reject Reservation'}</Typography>
          </Stack>
          <IconButton size="small" onClick={onClose} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* ── Content ── */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          <Stack spacing={2.5}>
            {/* Customer summary */}
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {reservation.customerName}
                  </Typography>
                  <Chip label={reservation.inquiryType} size="small" variant="outlined" />
                </Stack>
              </Box>
              <Divider />
              <Stack spacing={1.75} sx={{ px: 2, py: 2 }}>
                <InfoRow icon={<PhoneIcon fontSize="small" />} label="Phone" value={reservation.customerPhone} />
                {reservation.customerEmail && (
                  <InfoRow icon={<EmailIcon fontSize="small" />} label="Email" value={reservation.customerEmail} />
                )}
                <InfoRow icon={<CarIcon fontSize="small" />} label="Vehicle" value={vehicleLabel} />
                {(reservation.preferredDate || reservation.preferredTime) && (
                  <InfoRow
                    icon={<ScheduleIcon fontSize="small" />}
                    label="Preferred Date / Time"
                    value={[reservation.preferredDate, reservation.preferredTime].filter(Boolean).join(' at ')}
                  />
                )}
              </Stack>
            </Box>

            {/* Email notice */}
            {reservation.customerEmail ? (
              <Alert severity="info" icon={<EmailIcon fontSize="small" />} sx={{ borderRadius: 1.5 }}>
                A notification email will be sent to {reservation.customerEmail} once you confirm.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
                No email on file — the customer won't be notified automatically.
              </Alert>
            )}

            {/* Reason */}
            <TextField
              fullWidth
              required
              multiline
              rows={3}
              label={isApprove ? 'Note to include' : 'Reason for rejection'}
              placeholder={
                isApprove
                  ? 'e.g. Test drive confirmed for Saturday 10 AM at our main showroom'
                  : 'e.g. Requested vehicle is no longer available'
              }
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
              error={reasonError}
              helperText={
                reasonError
                  ? 'A note is required to confirm this decision.'
                  : 'Included in the email sent to the customer, and required to confirm this decision.'
              }
            />
          </Stack>
        </Box>

        <DrawerFooter>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={accentColor}
            onClick={handleConfirm}
            disabled={saving || reason.trim() === ''}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : isApprove ? <ApproveIcon /> : <RejectIcon />}
          >
            {saving ? 'Saving...' : isApprove ? 'Approve' : 'Reject'}
          </Button>
        </DrawerFooter>
      </Box>
    </Drawer>
  );
};

export default ReservationDecisionDrawer;
